<template lang="pug">
.product-images-swiper
  .master
    img(v-show="false" :src="variant.image_urls.large" :alt="variant.title")
    client-only
      image-zoom(
        message-pos="top"
        :title="variant.title"
        :regular="variant.image_urls.product"
        :zoom="variant.image_urls.large"
        :zoom-amount="2"
        @on-zoom="$amplitude.track('ZOOM_PRODUCT_IMAGE', {zone:'PRODUCT_DETAIL', product_id: variant.product_id, sku_id: variant.id})")

    .indicator
      .flex
        .variant.pf-ellipsis(v-if="variant" v-text="variant.name")
        .counter {{ `${currentIndex + 1}/${totalSize}`}}

  .thumbnails.hidden-sm-and-down
    swiper.swiper(
      ref="thumbnails"
      :options="thumbnailsOptions"
      :auto-destroy="false")
      swiper-slide.image(
        v-for="i, k in videos"
        :key="`video-${k}`")
        .contain(
          v-image="i.thumbnail"
          @click="handlePlayVideo(i, k)")
          strong
            i.bx.bx-play
      swiper-slide.image(
        v-for="i, k in images"
        :key="k"
        :class="{active: i.active}")
        .contain(
          v-image="i.thumbnail"
          :title="i.title"
          @click="onSlideClick(i, k)")
            span.promotion(v-if="i.variant.promotions && i.variant.promotions.length") {{ i.variant.promotions | promotionDiscount(false, true) }}

    template(v-if="images.length > 1")
      .swiper-btn-next(slot="button-next" @click="$refs.thumbnails.swiperInstance.slideNext()")
        i.bx.bx-chevron-right.bx-3.icon
      .swiper-btn-prev(slot="button-prev" @click="$refs.thumbnails.swiperInstance.slidePrev()")
        i.bx.bx-chevron-left.bx-3.icon

  video-player(
    v-if="video"
    :video="video"
    @close="handleClosePlayer"
  )
</template>

<script>
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import 'swiper/swiper-bundle.css'
import { mapVideoLinks } from '~/utilities/normalizers'
import VideoPlayer from '~/components/modals/VideoPlayer'

export default {
  components: {
    Swiper,
    SwiperSlide,
    [process.client && 'ImageZoom']: () => import('vue-image-zoomer'),
    VideoPlayer
  },

  scrollToTop: true,

  props: {
    product: {
      type: Object,
      default: null
    },

    variant: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      currentIndex: 0,
      videos: [],
      video: null
    }
  },

  computed: {
    thumbnailsOptions () {
      return {
        centerInsufficientSlides: true,
        centeredSlides: true,
        centeredSlidesBounds: true,
        spaceBetween: 12,
        slidesPerView: 5
      }
    },

    images () {
      if (!this.product) return []
      return Object
        .values(this.product.variants)
        .map(i => ({
          variant: i,
          image: i.image_urls.product,
          thumbnail: i.image_urls.thumbnail,
          title: i.name,
          active: this.variant && this.variant.id === i.id
        }))
    },

    totalSize () {
      if (!this.product) return 0
      return Object.keys(this.product.variants).length
    }
  },

  watch: {
    'variant.id': {
      handler (val) {
        if (!val) return
        const index = this.images.findIndex(i => i.variant.id === val)
        this.currentIndex = index
        this.navigate(this.videos.length + index)
      }
    }
  },

  mounted () {
    this.videos = mapVideoLinks(this.product.videos_attributes)

    const { id } = this.variant
    if (!id) return

    const index = this.images.findIndex(i => i.variant.id === id)
    this.$nextTick(() => { this.navigate(this.videos.length + index) })
  },

  methods: {
    handleClosePlayer () {
      this.video = null
    },

    handlePlayVideo (video) {
      this.video = video
    },

    navigate (index) {
      if (this.$refs.thumbnails) this.$refs.thumbnails.$swiper.slideTo(index, 600, false)
    },

    onSlideClick (image, key) {
      if (image?.variant) this.$emit('update:variant', image.variant)
    }
  }
}
</script>

<style lang="scss" scoped>
.product-images-swiper {
  ::v-deep {
    .vh--outer {
      width: 100%;
      height: auto;
      .vh--holder {
        picture:first-child {
          width: 100%;
          height: auto;
          img {
            width: 100%;
            height: auto;
            aspect-ratio: 1;
            object-fit: contain;
          }
        }

        .vh--message {
          top: 15px;
          right: 15px;
          background: rgba($--color-text-secondary, .75);
          padding: 0;
          width: 32px;
          height: 32px;
          border-radius: 32px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          .vh--icon {
            margin-right: 0;
          }
        }
      }
      .vh--loading-o {
        background: transparent;
      }
    }
    .vh--image {
      max-width: initial;
      object-fit: contain;
      aspect-ratio: 1;
    }
  }
  .master {
    font-size: 0;
    position: relative;
    border-radius: $--radius;
    overflow: hidden;
    .image {
      .contain {
        background-color: white;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        width: 100%;
        height: 0;
        padding-top: 100%;

      }
    }
    .indicator {
      position: absolute;
      z-index: 2;
      bottom: 1em;
      left: 1em;
      right: 1em;
      font-size: 13px;
      white-space: nowrap;
      .flex {
        justify-content: space-between
      }
      .variant,
      .counter {
        padding: .5em 1em;
        line-height: 1.2em;
        color: white;
        background: rgba($--color-text-secondary, .75);
        border-radius: 1000px;
      }
      .variant {
        max-width: 200px;
      }
    }
  }
  .thumbnails {
    margin-top: 15px;
    position: relative;
    .image {
      font-size: 0;
      padding: 1px;
      &.active {
        .contain {
          border-color: $--color-primary;
        }
      }
      .contain {
        cursor: pointer;
        background-color: white;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        border-radius: $--radius;
        border: 1px solid #efeffb;
        width: 100%;
        height: 0;
        padding-top: 100%;
        position: relative;
        position: relative;
        &:hover {
          strong {
            transform: translate(50%, -50%) scale(1.1);
            background: rgba(black, .3);
          }
        }
        strong {
          position: absolute;
          top: 50%;
          right: 50%;
          transform: translate(50%, -50%);
          background: rgba(black, .2);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 100%;
          font-size: 2rem;
          color: white;
          padding-left: .15em;
          transition: all .2s ease-in-out;
          backdrop-filter: blur(8px);
          pointer-events: none;
        }
        .promotion {
          position: absolute;
          top: 0;
          right: 0px;
          color: #fff;
          font-size: 10px;
          padding: 4px 5px;
          border-radius: $--radius;
          background: $--color-primary;
        }

      }
    }
    .swiper-container {
      width: calc(100% - 60px);
      margin: 0 auto;
    }
    .swiper-btn-prev,
    .swiper-btn-next {
      position: absolute;
      top: 35%;
      z-index: 10;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #DFDFEA;
      color: #988D98;
      padding: 5px;
      border-radius: 100%;
    }
    .swiper-btn-prev {
      left: 0px;
      right: auto;
    }
    .swiper-btn-next {
      right: 0px;
      left: auto;
    }
  }
}
</style>
